
const toSafeString = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const extractAccountIds = (user) => {
  if (Array.isArray(user.assignedAccounts) && user.assignedAccounts.length > 0) {
    return user.assignedAccounts.map((id) => String(id));
  }

  if (Array.isArray(user.accountIds) && user.accountIds.length > 0) {
    return user.accountIds.map((id) => String(id));
  }

  const accountItems = Array.isArray(user.accounts)
    ? user.accounts
    : Array.isArray(user.accountList)
      ? user.accountList
      : [];

  return accountItems
    .map((account) => account.accountId ?? account.id ?? account.accountCode)
    .filter((id) => id !== undefined && id !== null)
    .map((id) => String(id));
};

const extractProjectIds = (user) => {
  if (Array.isArray(user.assignedProjects) && user.assignedProjects.length > 0) {
    return user.assignedProjects.map((id) => String(id));
  }

  if (Array.isArray(user.projectIds) && user.projectIds.length > 0) {
    return user.projectIds.map((id) => String(id));
  }

  const projectItems = Array.isArray(user.projects)
    ? user.projects
    : Array.isArray(user.projectList)
      ? user.projectList
      : [];

  return projectItems
    .map((project) => project.projectId ?? project.id)
    .filter((id) => id !== undefined && id !== null)
    .map((id) => String(id));
};

export const formatRegisteredDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);

  const timePart = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
    .format(d)
    .replace("AM", "am")
    .replace("PM", "pm");

  return `${datePart}, ${timePart}`;
};

export const mapApiStatusToUiStatus = (status) => {
  const normalized = (status || "").trim().toUpperCase();
  if (normalized === "ACTIVE") return "Approved";
  if (normalized === "LOCKED") return "Locked";
  return "Pending";
};

export const mapApiUserToPendingUser = (user) => ({
  id: toSafeString(user.id) || toSafeString(user.userId),
  firstName: toSafeString(user.firstName),
  lastName: toSafeString(user.lastName),
  email: toSafeString(user.email),
  uid: toSafeString(user.userId),
  roleId: toSafeString(user.roleId),
  registeredAt: toSafeString(user.createdAt),
  status: mapApiStatusToUiStatus(toSafeString(user.status)),
  role: toSafeString(user.roleName) || null,
  accountIds: extractAccountIds(user),
  projectIds: extractProjectIds(user),
});

export const parseApiUsers = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === "object") {
    const obj = data ;
    if (Array.isArray(obj.data)) {
      return obj.data ;
    }
    return [data];
  }

  return [];
};

export const parseRoleOptions = (data) => {
  const arr = Array.isArray(data)
    ? (data)
    : Array.isArray((data )?.data)
      ? ((data )?.data)
      : [];

  return arr
    .map((item) => {
      const idRaw = item.roleId ?? item.id;
      const nameRaw = item.roleName ?? item.name ?? item.role;
      if (idRaw === undefined || idRaw === null || !nameRaw) return null;
      return { id: String(idRaw), name: String(nameRaw) };
    })
    .filter((item) => Boolean(item));
};

export const parseProjectOptions = (data) => {
  const asArray = (value) =>
    Array.isArray(value) ? value : [];

  const source =
    asArray(data).length > 0
      ? asArray(data)
      : asArray((data)?.data).length > 0
        ? asArray((data)?.data)
        : asArray((data)?.content);

  return source
    .map((item) => {
      const idRaw = item.projectId ?? item.id ?? item.code;
      const nameRaw = item.projectName ?? item.name ?? item.title;
      if (idRaw === undefined || idRaw === null) return null;
      return {
        id: String(idRaw),
        name: String(nameRaw ?? idRaw),
      };
    })
    .filter((item) => Boolean(item));
};

export const parseAccountOptions = (data) => {
  const asArray = (value) =>
    Array.isArray(value) ? value : [];

  const source =
    asArray(data).length > 0
      ? asArray(data)
      : asArray((data)?.data).length > 0
        ? asArray((data)?.data)
        : asArray((data)?.content).length > 0
          ? asArray((data)?.content)
          : asArray((data)?.items);

  return source
    .map((item) => {
      const idRaw =
        item.customerId ??
        item.accountId ??
        item.id ??
        item.accountCode ??
        item.customerCode;
      const nameRaw =
        item.customerName ??
        item.accountName ??
        item.name ??
        item.displayName ??
        item.accountCode ??
        item.customerCode;
      const cpUidRaw = item.cpUid ?? item.cpUID ?? item.clientPartnerUid ?? item.clientPartnerId;
      const dmUidRaw = item.dmUid ?? item.dmUID ?? item.deliveryManagerUid ?? item.deliveryManagerId;

      if (idRaw === undefined || idRaw === null || nameRaw === undefined || nameRaw === null) {
        return null;
      }

      return {
        id: String(idRaw),
        name: String(nameRaw),
        ...(cpUidRaw === undefined || cpUidRaw === null ? {} : { cpUid: String(cpUidRaw) }),
        ...(dmUidRaw === undefined || dmUidRaw === null ? {} : { dmUid: String(dmUidRaw) }),
      };
    })
    .filter((item) => item !== null);
};
